import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail, downloadExport } from "../lib/api";
import { PageHeader, EmptyState, Confirm } from "../components/Shared";
import { TableSkeleton } from "../components/Skeletons";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Sun, Plus, Trash2, Save, FileText, Pencil, Battery, Zap, PanelTop, Copy } from "lucide-react";

const blankApp = () => ({ name: "", watts: "", quantity: "1", hours_per_day: "", days_per_week: "7" });
const defaults = { name: "", notes: "", system_voltage: 24, sun_hours: 5, autonomy_days: 1, dod: 50, inverter_efficiency: 85, panel_watt: 400, battery_ah: 200, battery_voltage: 12, battery_type: "", battery_capacity_unit: "Ah", battery_kwh: "", include_cost: false, panel_price: "", battery_price: "", inverter_price: "", other_cost: "", project_id: "" };
const BATTERY_TYPES = [
  { v: "Lithium (LiFePO4)", dod: 80, ah: 100, volt: 24 }, { v: "Lithium-ion", dod: 80, ah: 100, volt: 24 }, { v: "Gel", dod: 50, ah: 200, volt: 12 },
  { v: "AGM", dod: 50, ah: 200, volt: 12 }, { v: "Flooded lead-acid", dod: 50, ah: 200, volt: 12 }, { v: "Tubular", dod: 60, ah: 220, volt: 12 },
  { v: "Lithium (LiFePO4) 48V", dod: 80, ah: 100, volt: 48 }, { v: "Lithium-ion 48V", dod: 80, ah: 100, volt: 48 }, { v: "Lead-acid 48V", dod: 50, ah: 150, volt: 48 },
];
const KWH_PRESETS = [
  { v: "Lithium 2.4 kWh module", kwh: 2.4 }, { v: "Lithium 5 kWh module", kwh: 5 },
  { v: "Lithium 10 kWh module", kwh: 10 }, { v: "Lithium 15 kWh module", kwh: 15 },
];
const PRESETS = [
  { name: "LED bulb", watts: 10, hours: 5, days: 7 }, { name: "Ceiling fan", watts: 60, hours: 8, days: 7 }, { name: "Standing fan", watts: 50, hours: 8, days: 7 },
  { name: "Fridge", watts: 150, hours: 24, days: 7 }, { name: "Freezer", watts: 200, hours: 24, days: 7 }, { name: "TV (LED)", watts: 80, hours: 5, days: 7 },
  { name: "Laptop", watts: 65, hours: 6, days: 6 }, { name: "Desktop PC", watts: 200, hours: 6, days: 6 }, { name: "Phone charger", watts: 10, hours: 3, days: 7 },
  { name: "Wi-Fi router", watts: 15, hours: 24, days: 7 }, { name: "Water pump", watts: 750, hours: 2, days: 4 }, { name: "Microwave", watts: 1000, hours: 0.5, days: 7 },
  { name: "Iron", watts: 1200, hours: 1, days: 3 }, { name: "Air conditioner", watts: 1500, hours: 6, days: 7 }, { name: "Security light", watts: 30, hours: 12, days: 7 },
  { name: "Printer", watts: 50, hours: 1, days: 5 }, { name: "Blender", watts: 400, hours: 0.5, days: 7 }, { name: "Decoder / Set-top box", watts: 25, hours: 5, days: 7 },
];
const n = (v) => (parseFloat(v) || 0);
const fmt = (v, u = "") => `${Math.round(v).toLocaleString()}${u ? ` ${u}` : ""}`;
const fmtFcfa = (v) => `${Math.round(v).toLocaleString()} FCFA`;

function compute(form, appliances) {
  let dailyWh = 0, peakW = 0;
  const rows = appliances.map((a) => {
    const conn = n(a.watts) * n(a.quantity);
    const dpw = Math.min(Math.max(a.days_per_week === "" || a.days_per_week == null ? 7 : n(a.days_per_week), 0), 7);
    const wh = conn * n(a.hours_per_day) * (dpw / 7);
    dailyWh += wh; peakW += conn;
    return { ...a, connected_w: conn, daily_wh: wh };
  });
  const sysv = n(form.system_voltage) || 24;
  const sun = n(form.sun_hours) || 5;
  const autonomy = Math.max(0.5, n(form.autonomy_days) || 1);
  const dod = Math.min(Math.max(n(form.dod) || 50, 10), 100) / 100;
  const invEff = Math.min(Math.max(n(form.inverter_efficiency) || 85, 50), 100) / 100;
  const panelW = Math.max(1, n(form.panel_watt) || 400);
  const battAh = Math.max(1, n(form.battery_ah) || 200);
  const battV = Math.max(1, n(form.battery_voltage) || 12);
  const dcWh = dailyWh / invEff;
  const battBankWh = dcWh * autonomy / dod;
  const battBankAh = battBankWh / sysv;
  const battBankKwh = battBankWh / 1000;
  let series, parallel, numBatteries;
  if (form.battery_capacity_unit === "kWh") {
    const unitWh = Math.max(0.001, n(form.battery_kwh)) * 1000;
    parallel = battBankWh > 0 ? Math.ceil(battBankWh / unitWh) : 0;
    series = 1; numBatteries = parallel;
  } else {
    series = Math.max(1, Math.round(sysv / battV));
    parallel = battBankAh > 0 ? Math.ceil(battBankAh / battAh) : 0;
    numBatteries = series * parallel;
  }
  const arrayW = sun > 0 ? dcWh / (sun * 0.8) : 0;
  const numPanels = arrayW > 0 ? Math.ceil(arrayW / panelW) : 0;
  const installedArrayW = numPanels * panelW;
  const inverterW = peakW > 0 ? Math.ceil((peakW * 1.25) / 100) * 100 : 0;
  const chargeA = sysv ? Math.round((installedArrayW / sysv) * 1.25 * 10) / 10 : 0;
  const costPanels = numPanels * n(form.panel_price);
  const costBatteries = numBatteries * n(form.battery_price);
  const costInverter = n(form.inverter_price);
  const otherCost = n(form.other_cost);
  const costTotal = costPanels + costBatteries + costInverter + otherCost;
  return { rows, dailyWh, peakW, battBankAh, battBankKwh, numBatteries, series, parallel, arrayW, numPanels, installedArrayW, inverterW, chargeA, costPanels, costBatteries, costInverter, otherCost, costTotal };
}

const ASSUMPTIONS = [
  { k: "system_voltage", label: "System voltage (V)" },
  { k: "sun_hours", label: "Peak sun hours (h/day)" },
  { k: "autonomy_days", label: "Days of autonomy" },
  { k: "dod", label: "Battery DoD (%)" },
  { k: "inverter_efficiency", label: "Inverter efficiency (%)" },
  { k: "panel_watt", label: "Panel rating (W)" },
];

export default function SolarCalculator() {
  const qc = useQueryClient();
  const [form, setForm] = useState({ ...defaults });
  const [apps, setApps] = useState([blankApp()]);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const { data: saved = [], isLoading } = useQuery({ queryKey: ["solar-sizings"], queryFn: () => api.get("/solar-sizings").then((r) => r.data) });
  const { data: projects = [] } = useQuery({ queryKey: ["projects-lite"], queryFn: () => api.get("/projects").then((r) => r.data) });
  const res = compute(form, apps);

  const setApp = (i, k, v) => setApps((s) => s.map((a, idx) => (idx === i ? { ...a, [k]: v } : a)));
  const addApp = () => setApps((s) => [...s, blankApp()]);
  const addPreset = (name) => {
    const p = PRESETS.find((x) => x.name === name);
    if (!p) return;
    const row = { name: p.name, watts: String(p.watts), quantity: "1", hours_per_day: String(p.hours ?? ""), days_per_week: String(p.days ?? 7) };
    setApps((s) => (s.length === 1 && !s[0].name.trim() && !s[0].watts ? [row] : [...s, row]));
  };
  const removeApp = (i) => setApps((s) => (s.length > 1 ? s.filter((_, idx) => idx !== i) : s));
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const reset = () => { setForm({ ...defaults }); setApps([blankApp()]); setEditingId(null); };

  const duplicate = (s) => {
    loadSizing(s);
    setEditingId(null);
    setForm((f) => ({ ...f, name: `${s.name} (copy)` }));
    toast.success("Loaded as a new copy — review and save.");
  };

  const loadSizing = (s) => {
    setEditingId(s.id);
    setForm({
      name: s.name || "", notes: s.notes || "", system_voltage: s.system_voltage, sun_hours: s.sun_hours,
      autonomy_days: s.autonomy_days, dod: s.dod, inverter_efficiency: s.inverter_efficiency,
      panel_watt: s.panel_watt, battery_ah: s.battery_ah, battery_voltage: s.battery_voltage, battery_type: s.battery_type || "",
      battery_capacity_unit: s.battery_capacity_unit || "Ah", battery_kwh: s.battery_kwh ? String(s.battery_kwh) : "",
      include_cost: !!s.include_cost, panel_price: s.panel_price ? String(s.panel_price) : "",
      battery_price: s.battery_price ? String(s.battery_price) : "", inverter_price: s.inverter_price ? String(s.inverter_price) : "",
      other_cost: s.other_cost ? String(s.other_cost) : "", project_id: s.project_id || "",
    });
    const list = (s.results?.appliances || []).map((a) => ({ name: a.name || "", watts: String(a.watts ?? ""), quantity: String(a.quantity ?? "1"), hours_per_day: String(a.hours_per_day ?? ""), days_per_week: String(a.days_per_week ?? "7") }));
    setApps(list.length ? list : [blankApp()]);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Give this sizing a name."); return; }
    if (!apps.some((a) => a.name.trim() && n(a.watts) > 0)) { toast.error("Add at least one appliance with a wattage."); return; }
    setSaving(true);
    const payload = {
      ...form,
      system_voltage: n(form.system_voltage), sun_hours: n(form.sun_hours), autonomy_days: n(form.autonomy_days),
      dod: n(form.dod), inverter_efficiency: n(form.inverter_efficiency), panel_watt: n(form.panel_watt),
      battery_ah: n(form.battery_ah), battery_voltage: n(form.battery_voltage),
      battery_capacity_unit: form.battery_capacity_unit || "Ah", battery_kwh: n(form.battery_kwh),
      include_cost: !!form.include_cost, panel_price: n(form.panel_price), battery_price: n(form.battery_price),
      inverter_price: n(form.inverter_price), other_cost: n(form.other_cost), project_id: form.project_id || "",
      appliances: apps.filter((a) => a.name.trim() || n(a.watts)).map((a) => ({ name: a.name, watts: n(a.watts), quantity: n(a.quantity), hours_per_day: n(a.hours_per_day), days_per_week: a.days_per_week === "" ? 7 : n(a.days_per_week) })),
    };
    try {
      if (editingId) await api.put(`/solar-sizings/${editingId}`, payload);
      else await api.post("/solar-sizings", payload);
      qc.invalidateQueries({ queryKey: ["solar-sizings"] });
      toast.success(editingId ? "Sizing updated." : "Sizing saved.");
      reset();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save."); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/solar-sizings/${id}`); qc.invalidateQueries({ queryKey: ["solar-sizings"] }); toast.success("Deleted."); }
    catch { toast.error("Could not delete."); }
  };

  const ResultCard = ({ icon: Icon, label, value, sub, testid }) => (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-slate-500 text-xs font-medium uppercase tracking-wide"><Icon className="h-4 w-4 text-amber-500" /> {label}</div>
      <div className="mt-1.5 text-2xl font-bold text-[#0B132B] font-mono-num">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="solar-page">
      <PageHeader title="Solar Calculator" subtitle="Size an off-grid solar system from a list of loads — panels, battery bank and inverter. Save and export a branded PDF." />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Sizing name *</Label><Input data-testid="solar-name" value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="e.g. Mr. Ngu residence — Buea" /></div>
              <div className="space-y-1.5"><Label>Link to project (optional)</Label>
                <Select value={form.project_id || "none"} onValueChange={(v) => setField("project_id", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="solar-project"><SelectValue placeholder="No project" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No project</SelectItem>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <Label className="text-sm font-semibold text-slate-700">Appliances / Loads</Label>
                <div className="w-56">
                  <Select value="" onValueChange={addPreset}>
                    <SelectTrigger data-testid="solar-preset-select" className="h-9"><SelectValue placeholder="+ Quick add appliance…" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {PRESETS.map((p) => <SelectItem key={p.name} value={p.name}>{p.name} · {p.watts} W · {p.hours}h × {p.days}d</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="overflow-x-auto rounded-lg border border-slate-100">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                    <tr><th className="px-3 py-2 text-left">Appliance</th><th className="px-2 py-2 text-right w-24">Watts</th><th className="px-2 py-2 text-right w-20">Qty</th><th className="px-2 py-2 text-right w-24">Hrs/day</th><th className="px-2 py-2 text-right w-24">Days/wk</th><th className="px-2 py-2 text-right w-28">Daily Wh</th><th className="w-10"></th></tr>
                  </thead>
                  <tbody>
                    {apps.map((a, i) => (
                      <tr key={i} className="border-t border-slate-100" data-testid={`solar-appliance-${i}`}>
                        <td className="px-2 py-1.5"><Input data-testid={`solar-appliance-name-${i}`} value={a.name} onChange={(e) => setApp(i, "name", e.target.value)} placeholder="e.g. LED bulb" /></td>
                        <td className="px-2 py-1.5"><Input type="number" min="0" step="any" className="text-right" data-testid={`solar-appliance-watts-${i}`} value={a.watts} onChange={(e) => setApp(i, "watts", e.target.value)} /></td>
                        <td className="px-2 py-1.5"><Input type="number" min="0" step="any" className="text-right" data-testid={`solar-appliance-qty-${i}`} value={a.quantity} onChange={(e) => setApp(i, "quantity", e.target.value)} /></td>
                        <td className="px-2 py-1.5"><Input type="number" min="0" step="any" className="text-right" data-testid={`solar-appliance-hours-${i}`} value={a.hours_per_day} onChange={(e) => setApp(i, "hours_per_day", e.target.value)} /></td>
                        <td className="px-2 py-1.5"><Input type="number" min="0" max="7" step="any" className="text-right" data-testid={`solar-appliance-days-${i}`} value={a.days_per_week} onChange={(e) => setApp(i, "days_per_week", e.target.value)} /></td>
                        <td className="px-3 py-2 text-right font-mono-num whitespace-nowrap text-slate-600">{fmt(n(a.watts) * n(a.quantity) * n(a.hours_per_day) * (Math.min(Math.max(a.days_per_week === "" ? 7 : n(a.days_per_week), 0), 7) / 7))}</td>
                        <td className="px-2 py-2 text-center"><button onClick={() => removeApp(i)} data-testid={`solar-appliance-remove-${i}`} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Button variant="outline" size="sm" className="mt-3" onClick={addApp} data-testid="solar-add-appliance"><Plus className="h-4 w-4 mr-2" /> Add appliance</Button>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <Label className="text-sm font-semibold text-slate-700">Design assumptions</Label>
            <div className="mt-3 space-y-1.5 max-w-xs">
              <Label className="text-xs text-slate-500">Battery type</Label>
              <Select value={form.battery_type || "none"} onValueChange={(v) => { const t = BATTERY_TYPES.find((x) => x.v === v); setForm((f) => ({ ...f, battery_type: v === "none" ? "" : v, ...(t ? { dod: t.dod, battery_ah: t.ah, battery_voltage: t.volt } : {}) })); }}>
                <SelectTrigger data-testid="solar-battery-type"><SelectValue placeholder="Select battery type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not specified</SelectItem>
                  {BATTERY_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.v} · {t.ah}Ah/{t.volt}V · {t.dod}% DoD</SelectItem>)}
                </SelectContent>
              </Select>
              {form.battery_type && <p className="text-xs text-slate-400">Prefills typical capacity, voltage and depth of discharge — you can still adjust them below.</p>}
            </div>
            <div className="mt-4 grid sm:grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Battery capacity unit</Label>
                <Select value={form.battery_capacity_unit || "Ah"} onValueChange={(v) => setField("battery_capacity_unit", v)}>
                  <SelectTrigger data-testid="solar-capacity-unit"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Ah">Ampere-hours (Ah)</SelectItem>
                    <SelectItem value="kWh">Kilowatt-hours (kWh)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.battery_capacity_unit === "kWh" ? (
                <>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-500">Battery capacity (kWh)</Label>
                    <Input type="number" min="0" step="any" data-testid="solar-field-battery_kwh" value={form.battery_kwh} onChange={(e) => setField("battery_kwh", e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-500">Quick module</Label>
                    <Select value="" onValueChange={(v) => { const m = KWH_PRESETS.find((x) => x.v === v); if (m) setForm((f) => ({ ...f, battery_kwh: String(m.kwh), battery_type: "Lithium (LiFePO4)", dod: 80 })); }}>
                      <SelectTrigger data-testid="solar-kwh-preset"><SelectValue placeholder="Pick a module…" /></SelectTrigger>
                      <SelectContent>{KWH_PRESETS.map((m) => <SelectItem key={m.v} value={m.v}>{m.v}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </>
              ) : (
                <>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-500">Battery capacity (Ah)</Label>
                    <Input type="number" min="0" step="any" data-testid="solar-field-battery_ah" value={form.battery_ah} onChange={(e) => setField("battery_ah", e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-500">Battery voltage (V)</Label>
                    <Input type="number" min="0" step="any" data-testid="solar-field-battery_voltage" value={form.battery_voltage} onChange={(e) => setField("battery_voltage", e.target.value)} />
                  </div>
                </>
              )}
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 mt-3">
              {ASSUMPTIONS.map((a) => (
                <div key={a.k} className="space-y-1.5">
                  <Label className="text-xs text-slate-500">{a.label}</Label>
                  <Input type="number" min="0" step="any" data-testid={`solar-field-${a.k}`} value={form[a.k]} onChange={(e) => setField(a.k, e.target.value)} />
                </div>
              ))}
            </div>
            <div className="mt-4 space-y-1.5"><Label className="text-xs text-slate-500">Notes</Label><Textarea rows={2} data-testid="solar-notes" value={form.notes} onChange={(e) => setField("notes", e.target.value)} placeholder="Optional note for the report…" /></div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" data-testid="solar-cost-toggle" checked={form.include_cost} onChange={(e) => setField("include_cost", e.target.checked)} className="h-4 w-4 accent-[#1E3A8A]" />
              <span className="text-sm font-semibold text-slate-700">Include cost estimate (FCFA)</span>
            </label>
            {form.include_cost && (
              <>
                <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 mt-3">
                  <div className="space-y-1.5"><Label className="text-xs text-slate-500">Panel unit price</Label><Input type="number" min="0" step="any" data-testid="solar-price-panel" value={form.panel_price} onChange={(e) => setField("panel_price", e.target.value)} /></div>
                  <div className="space-y-1.5"><Label className="text-xs text-slate-500">Battery unit price</Label><Input type="number" min="0" step="any" data-testid="solar-price-battery" value={form.battery_price} onChange={(e) => setField("battery_price", e.target.value)} /></div>
                  <div className="space-y-1.5"><Label className="text-xs text-slate-500">Inverter price</Label><Input type="number" min="0" step="any" data-testid="solar-price-inverter" value={form.inverter_price} onChange={(e) => setField("inverter_price", e.target.value)} /></div>
                  <div className="space-y-1.5"><Label className="text-xs text-slate-500">Installation / other</Label><Input type="number" min="0" step="any" data-testid="solar-price-other" value={form.other_cost} onChange={(e) => setField("other_cost", e.target.value)} /></div>
                </div>
                <div className="mt-3 flex items-center justify-between rounded-lg bg-slate-50 px-4 py-2.5 text-sm" data-testid="solar-cost-total">
                  <span className="text-slate-600">Estimated total ({res.numPanels} panels + {res.numBatteries} batteries + inverter{res.otherCost ? " + other" : ""})</span>
                  <span className="font-mono-num font-bold text-[#0B132B]">{fmtFcfa(res.costTotal)}</span>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-gradient-to-br from-[#0B132B] to-[#1C2541] rounded-xl p-5 text-white" data-testid="solar-summary">
            <div className="flex items-center gap-2 text-amber-300 text-sm font-semibold"><Sun className="h-5 w-5" /> System summary</div>
            <div className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-300">Daily energy (avg)</span><span className="font-mono-num font-semibold" data-testid="solar-result-daily">{fmt(res.dailyWh, "Wh")}</span></div>
              <div className="flex justify-between"><span className="text-slate-300">Peak load</span><span className="font-mono-num font-semibold">{fmt(res.peakW, "W")}</span></div>
            </div>
          </div>
          <ResultCard icon={PanelTop} label="Solar array" value={`${res.numPanels} panels`} sub={`${fmt(res.installedArrayW, "W")} total (${form.panel_watt || 0} W each)`} testid="solar-result-array" />
          <ResultCard icon={Battery} label="Battery bank" value={`${res.numBatteries} batteries`} sub={form.battery_capacity_unit === "kWh" ? `${res.battBankKwh.toFixed(1)} kWh total` : `${fmt(res.battBankAh, "Ah")} @ ${form.system_voltage || 0} V · ${res.series}S × ${res.parallel}P`} testid="solar-result-battery" />
          <ResultCard icon={Zap} label="Inverter (min)" value={fmt(res.inverterW, "W")} sub={`Charge controller ≈ ${res.chargeA} A`} testid="solar-result-inverter" />
          {form.include_cost && res.costTotal > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="solar-result-cost">
              <div className="flex items-center gap-2 text-amber-700 text-xs font-medium uppercase tracking-wide"><Zap className="h-4 w-4" /> Estimated cost</div>
              <div className="mt-1.5 text-2xl font-bold text-amber-900 font-mono-num">{fmtFcfa(res.costTotal)}</div>
            </div>
          )}
          <div className="flex gap-2">
            <Button onClick={save} disabled={saving} data-testid="solar-save" className="flex-1 bg-[#1E3A8A] hover:bg-[#1D4ED8]"><Save className="h-4 w-4 mr-2" /> {editingId ? "Update" : "Save sizing"}</Button>
            {editingId && <Button variant="outline" onClick={reset} data-testid="solar-cancel-edit">Cancel</Button>}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 font-semibold text-slate-700">Saved sizings</div>
        {isLoading ? <TableSkeleton cols={6} /> : saved.length === 0 ? (
          <EmptyState text="No saved sizings yet. Build one above and click Save." />
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Name</TableHead><TableHead className="text-right">Daily Wh</TableHead><TableHead>Array</TableHead><TableHead>Battery</TableHead><TableHead>Inverter</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {saved.map((s) => { const r = s.results || {}; return (
                <TableRow key={s.id} data-testid="solar-saved-row">
                  <TableCell className="font-medium text-slate-800">{s.name}</TableCell>
                  <TableCell className="text-right font-mono-num">{fmt(s.daily_wh || 0)}</TableCell>
                  <TableCell className="text-sm text-slate-600">{r.num_panels || 0} × {s.panel_watt}W</TableCell>
                  <TableCell className="text-sm text-slate-600">{r.num_batteries || 0} × {s.battery_capacity_unit === "kWh" ? `${s.battery_kwh}kWh` : `${s.battery_ah}Ah`}</TableCell>
                  <TableCell className="text-sm text-slate-600">{fmt(r.inverter_w || 0, "W")}</TableCell>
                  <TableCell className="text-sm text-slate-500">{(s.created_at || "").slice(0, 10)}</TableCell>
                  <TableCell className="text-right">
                    <div className="stems-actions">
                      <Button size="sm" variant="outline" onClick={() => downloadExport(`/export/solar-sizing/${s.id}.pdf`, `solar_${s.name}.pdf`)} data-testid={`solar-pdf-${s.id}`} title="Export PDF"><FileText className="h-4 w-4" /></Button>
                      <Button size="sm" variant="outline" onClick={() => duplicate(s)} data-testid={`solar-duplicate-${s.id}`} title="Duplicate"><Copy className="h-4 w-4" /></Button>
                      <Button size="sm" variant="outline" onClick={() => loadSizing(s)} data-testid={`solar-edit-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Confirm onConfirm={() => remove(s.id)} title="Delete sizing?" description={`This removes "${s.name}".`} trigger={<Button size="sm" variant="outline" className="text-red-600" data-testid={`solar-delete-${s.id}`}><Trash2 className="h-4 w-4" /></Button>} />
                    </div>
                  </TableCell>
                </TableRow>
              ); })}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
